<!--
  AdminSMTP.svelte — SMTP email configuration page.

  Provides a form for the admin to configure SMTP mail delivery settings
  (host, port, TLS, credentials, sender identity).  Also offers:
    - "Verify Connection" — tests the SMTP handshake without sending.
    - "Send Test Email" — delivers a test message to a chosen address.

  Uses the AppNetwork `updateSMTP`, `verifySMTP`, and `testSMTP` functions.
-->
<script lang="ts">
	import { Button, Input, Label, Toggle, Heading, Spinner, Helper, Badge, Modal } from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import { onMount } from 'svelte';
	import { getSettings, updateSMTP, testSMTP, verifySMTP } from '../../AppNetwork';
	import type { Settings } from '../../AppTypes';
	import { toastSuccess, toastError } from '../../toast';
	import { t, t2 } from '../../i18n';

	/** Current settings loaded from server. */
	let settings: Settings | null = null;
	/** Whether the initial load is in progress. */
	let loading = $state(true);
	/** Whether a save operation is in progress. */
	let saving = $state(false);
	/** Whether a verify operation is in progress. */
	let verifying = $state(false);
	/** Whether a test-send operation is in progress. */
	let testing = $state(false);
	/** Error message from the last operation. */
	let error = $state('');

	// ── Form fields ──────────────────────────────────────────────

	/** SMTP server hostname. */
	let smtpHost = $state('');
	/** SMTP server port. */
	let smtpPort = $state('');
	/** Whether to use TLS for the SMTP connection. */
	let smtpSecure = $state(false);
	/** SMTP authentication username. */
	let smtpUser = $state('');
	/** SMTP authentication password. */
	let smtpPassword = $state('');
	/** Sender email address for the From header. */
	let fromEmail = $state('');
	/** Sender display name for the From header. */
	let fromName = $state('');
	/** Whether SMTP has been verified. */
	let isSMTPVerified = $state(false);

	/** Whether the test-email modal is open. */
	let showTestModal = $state(false);
	/** Email address to send the test message to. */
	let testEmailAddress = $state('');

	/**
	 * Load the current settings from the server and populate the SMTP form.
	 */
	onMount(async () => {
		try {
			settings = await getSettings();
			if (settings) populateForm(settings);
			else error = t2('No settings found. Save defaults to create the settings row.');
		} catch (err) {
			error = err instanceof Error ? err.message : t2('Failed to load settings');
		} finally {
			loading = false;
		}
	});

	/**
	 * Populate SMTP form fields from a Settings object.
	 * @param s - The settings object to read from.
	 */
	function populateForm(s: Settings): void {
		smtpHost = s.smtpHost || '';
		smtpPort = s.smtpPort || '';
		smtpSecure = s.smtpSecure;
		smtpUser = s.smtpUser || '';
		smtpPassword = s.smtpPassword || '';
		fromEmail = s.fromEmail || '';
		fromName = s.fromName || '';
		isSMTPVerified = s.isSMTPVerified;
	}

	/**
	 * Save the current SMTP configuration to the server.
	 */
	async function handleSave(): Promise<void> {
		saving = true;
		error = '';

		/** Persist the SMTP configuration and refresh the form on success. */
		try {
			settings = await updateSMTP({
				smtpHost,
				smtpPort,
				smtpSecure,
				smtpUser,
				smtpPassword,
				fromEmail,
				fromName
			});
			populateForm(settings);
			toastSuccess(t2('SMTP settings saved successfully.'));
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Failed to save SMTP settings'));
		} finally {
			saving = false;
		}
	}

	/**
	 * Verify the SMTP connection by performing a handshake.
	 * Saves the current form values first so the server tests the latest config.
	 */
	async function handleVerify(): Promise<void> {
		verifying = true;
		error = '';

		/** Save then verify the SMTP handshake to confirm connectivity. */
		try {
			/* Save current form values so the server uses them for the test. */
			settings = await updateSMTP({
				smtpHost,
				smtpPort,
				smtpSecure,
				smtpUser,
				smtpPassword,
				fromEmail,
				fromName
			});
			populateForm(settings);

			await verifySMTP();
			isSMTPVerified = true;
			toastSuccess(t2('SMTP connection verified successfully.'));
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('SMTP verification failed'));
		} finally {
			verifying = false;
		}
	}

	/**
	 * Send a test email to the specified address.
	 * Saves the current form values first so the server uses the latest config.
	 */
	async function handleTestSend(): Promise<void> {
		if (!testEmailAddress) return;
		testing = true;
		error = '';

		/** Save then send a test email to the specified address. */
		try {
			/* Save current form values so the server uses them for the test. */
			settings = await updateSMTP({
				smtpHost,
				smtpPort,
				smtpSecure,
				smtpUser,
				smtpPassword,
				fromEmail,
				fromName
			});
			populateForm(settings);

			await testSMTP(testEmailAddress);
			toastSuccess(t2('Test email sent to {{email}}.', { email: testEmailAddress }));
			showTestModal = false;
			testEmailAddress = '';
		} catch (err) {
			toastError(err instanceof Error ? err.message : t2('Failed to send test email'));
		} finally {
			testing = false;
		}
	}
</script>

<Heading tag="h3">{$t("SMTP Configuration")}</Heading>

{#if loading}
	<div class="flex items-center justify-center py-12">
		<Spinner size="8" />
	</div>
{:else}
	<div class="mt-4 space-y-6">
		{#if error}
			<div class="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
				<i class="fa-solid fa-exclamation-triangle me-2"></i>{error}
			</div>
		{/if}

		<!-- Status -->
		<AbstractCard class="max-w-none">
			<div class="flex items-center justify-between">
				<div class="flex items-center gap-3">
					<i class="fa-solid fa-envelope text-2xl text-gray-500 dark:text-gray-400"></i>
					<div>
						<p class="font-medium text-gray-900 dark:text-white">{$t("SMTP Status")}</p>
						<p class="text-sm text-gray-500 dark:text-gray-400">
							{smtpHost ? `${smtpHost}:${smtpPort}` : $t('Not configured')}
						</p>
					</div>
				</div>
				<Badge color={isSMTPVerified ? 'green' : 'yellow'} large>
					<i class="fa-solid {isSMTPVerified ? 'fa-check-circle' : 'fa-exclamation-circle'} me-1"></i>
					{isSMTPVerified ? $t('Verified') : $t('Unverified')}
				</Badge>
			</div>
		</AbstractCard>

		<!-- Server Settings -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-server me-2"></i>{$t("Server")}
			</h4>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div>
					<Label for="smtpHost" class="mb-2">{$t("SMTP Host")}</Label>
					<Input id="smtpHost" bind:value={smtpHost} placeholder="smtp.example.com" />
				</div>

				<div>
					<Label for="smtpPort" class="mb-2">{$t("SMTP Port")}</Label>
					<Input id="smtpPort" bind:value={smtpPort} placeholder="587" />
					<Helper class="mt-1">{$t("Common ports: 587 (STARTTLS), 465 (TLS), 25 (plain).")}</Helper>
				</div>

				<div class="flex items-center gap-3 md:col-span-2">
					<Toggle bind:checked={smtpSecure} />
					<div>
						<p class="font-medium text-gray-900 dark:text-white">{$t("Use TLS")}</p>
						<p class="text-sm text-gray-500 dark:text-gray-400">
							{$t("Enable for port 465. For 587 with STARTTLS leave this off.")}
						</p>
					</div>
				</div>
			</div>
		</AbstractCard>

		<!-- Authentication -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-key me-2"></i>{$t("Authentication")}
			</h4>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div>
					<Label for="smtpUser" class="mb-2">{$t("Username")}</Label>
					<Input id="smtpUser" bind:value={smtpUser} placeholder="user@example.com" />
				</div>

				<div>
					<Label for="smtpPassword" class="mb-2">{$t("Password")}</Label>
					<Input id="smtpPassword" type="password" bind:value={smtpPassword} placeholder="••••••••" />
				</div>
			</div>
		</AbstractCard>

		<!-- Sender Identity -->
		<AbstractCard class="max-w-none">
			<h4 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
				<i class="fa-solid fa-id-card me-2"></i>{$t("Sender Identity")}
			</h4>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div>
					<Label for="fromEmail" class="mb-2">{$t("From Email")}</Label>
					<Input id="fromEmail" bind:value={fromEmail} placeholder="noreply@example.com" />
					<Helper class="mt-1">{$t("The sender address in the email From header.")}</Helper>
				</div>

				<div>
					<Label for="fromName" class="mb-2">{$t("From Name")}</Label>
					<Input id="fromName" bind:value={fromName} placeholder="AbstractID" />
					<Helper class="mt-1">{$t("Display name next to the sender address.")}</Helper>
				</div>
			</div>
		</AbstractCard>

		<!-- Actions -->
		<div class="flex flex-wrap items-center justify-between gap-3">
			<div class="flex gap-3">
				<Button color="alternative" onclick={handleVerify} disabled={verifying || !smtpHost}>
					{#if verifying}
						<i class="fa-solid fa-spinner fa-spin me-2"></i>{$t("Verifying...")}
					{:else}
						<i class="fa-solid fa-plug me-2"></i>{$t("Verify Connection")}
					{/if}
				</Button>

				<Button
					color="alternative"
					onclick={() => {
						showTestModal = true;
					}}
					disabled={!smtpHost}
				>
					<i class="fa-solid fa-paper-plane me-2"></i>{$t("Send Test Email")}
				</Button>
			</div>

			<Button onclick={handleSave} disabled={saving}>
				{#if saving}
					<i class="fa-solid fa-spinner fa-spin me-2"></i>{$t("Saving...")}
				{:else}
					<i class="fa-solid fa-save me-2"></i>{$t("Save SMTP")}
				{/if}
			</Button>
		</div>
	</div>
{/if}

<!-- Test Email Modal -->
<Modal title={$t("Send Test Email")} bind:open={showTestModal} size="sm">
	<div class="space-y-4">
		<p class="text-gray-600 dark:text-gray-300">{$t("Enter the email address to send a test message to.")}</p>
		<div>
			<Label for="testEmail" class="mb-2">{$t("Recipient Email")}</Label>
			<Input id="testEmail" type="email" bind:value={testEmailAddress} placeholder="test@example.com" />
		</div>
	</div>
	{#snippet footer()}
	
			<Button onclick={handleTestSend} disabled={testing || !testEmailAddress}>
				{#if testing}
					<i class="fa-solid fa-spinner fa-spin me-2"></i>{$t("Sending...")}
				{:else}
					<i class="fa-solid fa-paper-plane me-2"></i>{$t("Send")}
				{/if}
			</Button>
			<Button
				color="alternative"
				onclick={() => {
					showTestModal = false;
				}}>{$t("Cancel")}</Button
			>
		
	{/snippet}
</Modal>
